import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'onDark' | 'onDarkGhost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  children: ReactNode;
  className?: string;
}

function classNames(variant: ButtonVariant, size: ButtonSize, block?: boolean, extra?: string) {
  return [styles.button, styles[variant], styles[size], block ? styles.block : '', extra ?? ''].filter(Boolean).join(' ');
}

export interface ButtonProps extends CommonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'> {
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  block,
  iconStart,
  iconEnd,
  loading,
  children,
  className,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={classNames(variant, size, block, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : iconStart}
      <span>{children}</span>
      {!loading && iconEnd}
    </button>
  );
}

export interface LinkButtonProps extends CommonProps {
  to: string;
  onClick?: () => void;
}

export function LinkButton({ variant = 'primary', size = 'md', block, iconStart, iconEnd, children, className, to, onClick }: LinkButtonProps) {
  return (
    <Link to={to} viewTransition className={classNames(variant, size, block, className)} onClick={onClick}>
      {iconStart}
      <span>{children}</span>
      {iconEnd}
    </Link>
  );
}
